import React from 'react';
import { array, func, object } from 'prop-types';
import { connect } from 'react-redux';
import { orderBy } from 'lodash';
import template from 'url-template';
import { withRouter } from 'react-router-dom';

import { fetchProfile, putProfile } from '../../actions/profileActions';
import { enqueueSnackbar } from '../../actions/applicationMessageActions';
import {
    fetchProfileObjects,
    reorderProfileObjects,
    toggleSelectProfileObject,
    updateProfileObject
} from '../../actions/profileObjectActions';
import { isAllowed } from '../../helpers/auth';
import { Permissions } from '../../helpers/permissions';

import ActionBar from '../../components/ActionBar';
import ActionBarItem from '../../components/ActionBarItem';
import CreateProfileObjectModal from '../Modals/CreateProfileObjectModal';
import DeleteProfileObjectModal from '../Modals/DeleteProfileObjectModal';
import EditProfileObjectModal from '../Modals/EditProfileObjectModal';
import EsmsPage from '../../components/EsmsPage';
import ManageProfilePageSidebar from './ManageProfilePageSidebar';
import ProfileObjectsTable from './ProfileObjectsTable';

const ProfilesUrl = template.parse('/iothub/profiles{?company}');

class ManageProfilePage extends React.Component {
    static defaultProps = {
        companyInfo:            {},
        fetchProfile:           () => {},
        fetchProfileObjects:    () => {},
        location:               {},
        selectedProfileObjects: [],
        updateProfileObject:    () => {}
    }

    static propTypes = {
        companyInfo:               object,
        currentScopes:             array.isRequired,
        enqueueSnackbar:           func,
        fetchProfile:              func,
        fetchProfileObjects:       func,
        history:                   object,
        location:                  object,
        putProfile:                func,
        reorderProfileObjects:     func,
        selectedProfile:           object,
        selectedProfileObjects:    array,
        toggleSelectProfileObject: func,
        updateProfileObject:       func
    }

    state = {
        objectsToDelete:       [],
        objectToEdit:          undefined,
        showCreateObjectModal: false,
        showDeleteModal:       false,
        showEditModal:         false
    }

    async componentDidMount() {

        const canReadProfiles = isAllowed(this.props.currentScopes, Permissions('iothubRead'));
        if (!canReadProfiles) {
            this.redirectToProfiles();
            return;
        }
        const { fetchProfile, fetchProfileObjects } = this.props;
        const values = new URLSearchParams(this.props.location.search);
        try {
            if (values.has('profile')) {
                await fetchProfile(values.get('profile'));
                await fetchProfileObjects(values.get('profile'));
            } else {
                this.redirectToProfiles();
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
            this.props.enqueueSnackbar(
                `An error has occurred: ${error.message}`,
                'error'
            );
        }
    }

    handleCloseDeleteModal = () => {
        this.setState({
            showDeleteModal: false,
            objectsToDelete: []
        });
    }

    handleCloseEditModal = () => {
        this.setState({
            objectToEdit:  undefined,
            showEditModal: false
        });
    }

    handleDeleteSelected = () => {
        this.setState({
            showDeleteModal: true
        });
    }

    handleCreateObjectModal = toggleState => {
        this.setState(state => ({
            showCreateObjectModal: toggleState
        }));
    }

    handleShowDeleteModal = (profileObject) => {
        this.setState({
            showDeleteModal: true,
            objectsToDelete: [profileObject]
        });
    }

    handleShowEditModal = (objectToEdit) => {
        this.setState({
            objectToEdit,
            showEditModal: true
        });
    }

    handleSort = (property) => {
        return (event) => {
            this.props.reorderProfileObjects(property);
        };
    }

    redirectToIotHub = () => {
        const { history, location } = this.props;

        history.push('/iothub' + location.search);
    }

    redirectToProfiles = () => {
        const values = new URLSearchParams(this.props.location.search);
        this.props.history.push(
            ProfilesUrl.expand({ company: values.get('company') })
        );
    }

    render() {
        const {
            companyInfo,
            selectedProfile,
            toggleSelectProfileObject,
            selectedProfileObjects,
            updateProfileObject
        } = this.props;
        if (!selectedProfile) {
            return null;
        }
        const isFullProfileAllowed = isAllowed(this.props.currentScopes, Permissions('iothubFull'));
        let profileObjectsToDelete = this.state.objectsToDelete;
        if (!profileObjectsToDelete.length) {
            profileObjectsToDelete = [...selectedProfileObjects];
        }

        return (
            <EsmsPage
                child={selectedProfile.name}
                handlers={[this.redirectToIotHub, this.redirectToProfiles]}
                parents={[companyInfo.companyName, 'Profiles']}
                sidebar={(
                    <ManageProfilePageSidebar
                        profile={selectedProfile}
                        onUpdate={this.props.putProfile}
                        disabled={!isFullProfileAllowed}
                    />
                )}
                title="Profile Objects"
            >
                <ActionBar
                    onSearch={this.handleSearch}
                >
                    <ActionBarItem
                        onClick={() => this.handleCreateObjectModal(true)}
                        type="add"
                        hidden={!isFullProfileAllowed}
                    >
                        Add Profile Object
                    </ActionBarItem>
                    <ActionBarItem
                        disabled={!selectedProfileObjects.length}
                        onClick={this.handleDeleteSelected}
                        type="delete"
                        hidden={!isFullProfileAllowed}
                    >
                        Delete Profile Objects
                    </ActionBarItem>
                </ActionBar>
                <ProfileObjectsTable
                    isFullProfileAllowed={isFullProfileAllowed}
                    profileObjects={this.props.profileObjects}
                    onDelete={this.handleShowDeleteModal}
                    onItemChange={updateProfileObject}
                    onItemToggle={toggleSelectProfileObject}
                    onManage={this.handleShowEditModal}
                    onSort={this.handleSort}
                    order={this.props.order}
                    orderBy={this.props.orderBy}
                    type={selectedProfile.basType}
                />
                <EditProfileObjectModal
                    open={this.state.showEditModal}
                    close={this.handleCloseEditModal}
                    object={this.state.objectToEdit}
                    title="Edit Profile Object"
                />
                <DeleteProfileObjectModal
                    open={this.state.showDeleteModal}
                    onClose={this.handleCloseDeleteModal}
                    profileObjects={profileObjectsToDelete}
                    profileId={selectedProfile.id}
                    profileName={selectedProfile.name}
                />
                <CreateProfileObjectModal
                    open={this.state.showCreateObjectModal}
                    close={() => this.handleCreateObjectModal(false)}
                    title="Add New Profile Object"
                />
            </EsmsPage>
        );
    }
}

export const mapStateToProps = ({ companyInfo, core, ...state }) => {
    const { profiles, selectedProfileId } = state.profile;
    const { order, orderBy: orderByKey, profileObjects } = state.profileObjects;
    const selectedProfile = profiles.find(({ id }) => id === selectedProfileId);
    const selectedProfileObjects = profileObjects.filter(({ isSelected }) => isSelected);

    return {
        companyInfo,
        currentScopes:  core.currentScopes,
        order,
        orderBy:        orderByKey,
        profileObjects: orderBy(profileObjects, [orderByKey], order),
        selectedProfile,
        selectedProfileObjects
    };
};

const mapDispatchToProps = {
    enqueueSnackbar,
    fetchProfile,
    fetchProfileObjects,
    putProfile,
    reorderProfileObjects,
    toggleSelectProfileObject,
    updateProfileObject
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ManageProfilePage));
